import { actions, services } from '@ehi/global-marketing-interface';
import utils from 'utils';
import { i18n } from 'utils/i18n';
import { dispatchEventWithDOM } from 'utils/analytics';
import { isCrosLoginAccountError } from 'utils/profile';
import WINDOW_OBJECT_KEYS from 'constants/windowObjectKeys';
import RECAPTCHA from 'constants/recaptcha';
import GMA_RESPONSE_CODES from 'constants/gmaResponseCodes';
import { gmiRequest } from 'actions/request';
import { profileDataSelector } from 'selectors/profileSelectors';
import { driverInfoPhonePayloadParser } from 'utils/driverProfilePayloadParser';

/**
 * createProfile action
 * gmi service call to create a new user profile on enrollment
 *
 * @param {object} params
 * @param {string} params.first_name - First Name
 * @param {string} params.last_name - Last Name
 * @param {string} params.password - Password
 * @param {string} params.email - Email
 * @param {string} params.user_name - Username (should use value entered as user's email)
 * @param {string} params.request_origin_channel - GBO source constant - same for all channels on enrollment
 * @param {string} params.address.country_code - User's Address Country code
 * @param {string} params.address.country_name - User's Address Country name
 * @param {boolean} params.terms_and_conditions.accept_decline - Whether user accepted T&C or not - should be true
 * @param {string} params.terms_and_conditions.accept_decline_date - Date of T&C acceptance by user (YYYY-MM-DD)
 * @param {string} params.preference.source_code - GBO sourced constant - source channel code (i.e. ARNTCONF1)
 * @param {boolean} params.preference.email_preference.rental_receipts - Whether user wants to receive Alamo emails or not
 * @param {boolean} params.preference.email_preference.special_offers - Whether user wants to receive Alamo emails or not
 * @return {Promise}
 */
export const createProfile = (payload) => (dispatch) =>
  dispatch(
    gmiRequest({
      loadingOverlay: true,
      data: {
        payload,
      },
      opts: {
        actionName: RECAPTCHA.ACTION_NAMES.CREATE_PROFILE,
        actionType: RECAPTCHA.ACTION_TYPE_RECAPTCHA,
      },
      service: services.gma.profile.createProfile,
    })
  );

/**
 * modifyProfile action
 * gmi service call to modify a user profile on enrollment
 *

 * This potentially dispatches two actions.
 * If a password is present in the payload, this will attempt to update the password at endpoint: `profile/password`
 * If a password was not present, this action is skipped.
 * Next, if any other profile modifications are present, this will attempt to update through `services.gma.profile.modify` which is basically `profile/modify`
 * If there is an error upon either endpoint, `.catch()` will spring into action and throw an additional error
 * We throw this additional error in the case that a subsequent catch (such as the one in our edit modal component) will be triggered as well and let us handle it.
 *
 * @param {object} payload
 * @param {string} payload.date_of_birth
 * @param {string} payload.password - New password, sent in a separate request
 *
 * @param {object} payload.address - Profile's Address Data
 * @param {string} payload.address.address_type
 * @param {string} payload.address.city
 * @param {string} payload.address.country_code
 * @param {string} payload.address.country_name
 * @param {string} payload.address.country_subdivision_code
 * @param {string} payload.address.country_subdivision_name
 * @param {string} payload.address.postal - Postal or Zip Code
 * @param {array<string>} payload.address.street_addresses
 *
 * @param {object} payload.contact - Profile's Contact Data
 * @param {string} payload.contact.email
 * @param {array<object>} payload.contact.phones
 *
 * @param {object} payload.drivers_license - Profile's Driver License Data
 * @param {string} payload.drivers_license.birth_date
 * @param {string} payload.drivers_license.country_code
 * @param {string} payload.drivers_license.country_name
 * @param {string} payload.drivers_license.country_subdivision_code
 * @param {string} payload.drivers_license.country_subdivision_name
 * @param {string} payload.drivers_license.license_expiration_date
 * @param {string} payload.drivers_license.license_issue_date
 * @param {string} payload.drivers_license.license_number
 *
 * @param {object} payload.preference - Profile's Email Preferences
 * @param {boolean} payload.preference.email_preference.rental_receipts
 * @param {boolean} payload.preference.email_preference.special_offers

 * @return {Promise}
 */
export const modifyProfile = (payload) => (dispatch) => {
  const { password, ...payloadForModify } = payload;

  if (payload.contact?.phones?.length > 0 && !utils.fieldValidation.isMasked(payload.contact.phones[0]?.phone_number)) {
    payloadForModify.driver_info = driverInfoPhonePayloadParser(payload.contact.phones[0]);
  }
  const opts = {
    modes: {
      update: true,
    },
  };

  return new Promise((resolve) => {
    // first Promise will only handle password updates
    // if anything goes wrong, we throw an error and abandon the rest of the chain
    // if there are no password updates, we just resolve
    if (password) {
      resolve(
        dispatch(
          gmiRequest({
            loadingOverlay: true,
            action: actions.request({
              data: password,
              service: () => ({
                // no existing password service, have to mock our own so its tracked in redux
                method: 'POST',
                origin: utils.config.getUrl('gmaUrl'),
                endpoint: 'profile/password',
                withCredentials: true,
                data: password,
              }),
            }),
            timeout: true,
          })
        )
      );
      return;
    }
    resolve(true);
  })
    .then((response) => {
      if (response.messages) {
        // to handle 'CROS_LOGIN_ACCOUNT_ERROR' warning
        const loginErrors = response.messages.filter((msg) => !isCrosLoginAccountError(msg.code, msg.priority));
        if (loginErrors.length > 0) {
          throw new Error(true);
        }
        return Promise.resolve(true);
      }

      return Promise.resolve(true);
    })
    .then(() => {
      // only called if preference was modified
      if (!utils.gmi.isObjectEmpty(payloadForModify)) {
        return dispatch(
          gmiRequest({
            loadingOverlay: true,
            action: actions.request({
              data: {
                payload: payloadForModify,
                opts,
              },
              service: services.gma.profile.modify,
            }),
          })
        );
      }

      return Promise.resolve(true);
    })
    .then((response) => {
      if (response.messages) {
        // throw new Error('error');
        return response;
      }

      return Promise.resolve(true);
    })
    .catch((e) => {
      // intentionally re-throwing the error here so another catch will grab it
      throw e;
    });
};

/**
 * getNonLoyaltyProfile action
 * gmi service call to get non loyalty profile object attached to given individual ID
 *
 * @param {object} individualId - Profile individual ID
 * @return {Promise}
 */
export const getNonLoyaltyProfile = (individual_id) => (dispatch) =>
  dispatch(
    gmiRequest({
      loadingOverlay: true,
      action: actions.request({
        data: {
          endpoint: { individual_id },
        },
        service: services.gma.profile.getNLProfile,
      }),
    })
  );

/**
 * searchRenterByMembership action
 * gmi service call to get loyalty profile object by last name and loyalty number
 *
 * @param {object} payload
 * @param {string} payload.last_name - Driver's last name
 * @param {string} payload.loyalty_number - Profile Loyalty Number
 * @return {Promise}
 */
export const searchRenterByMembership = (payload) => (dispatch) =>
  dispatch(
    gmiRequest({
      loadingOverlay: true,
      action: actions.request({
        data: { payload },
        service: services.gma.profile.searchRenterByMembership,
      }),
    })
  );

/**
 * @name searchProfileByDriverLicense
 * @desc Dispatcher for the GMA request that looks up a driver profile outside of check-in.
 *
 * @param {object} payload
 * @param {string} payload.country
 * @param {string} payload.country_subdivision
 * @param {string} payload.driver_license_number
 * @param {string} payload.last_name
 *
 * @returns {Function}
 */
export const searchProfileByDriverLicense = (payload, isOci) => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.profile.searchProfileByDriverLicense,
        data: {
          payload,
          params: { enableAlamoInsider: utils.config.getIsEnableAlamoInsider() },
          endpoint: { is_oci: isOci },
        },
      }),
      loadingOverlay: true,
    })
  );

/**
 * @name lookupContactProfileForAuthUser
 * @desc Dispatches a search profile by DL action with data from a Drivers License form
 *  and returns an object containing info for a profile merge operation.
 *
 * @param carryoverData {object} - Data to be used on the lookup
 * @param carryoverData.country_code {string}
 * @param carryoverData.country_subdivision_code {string}
 * @param carryoverData.driver_license_number {string}
 *
 * @returns {function}
 */
export const lookupContactProfileForAuthUser = (carryoverData) => (dispatch, getState) => {
  const state = getState();
  const { drivers_license } = carryoverData;
  const { last_name } = profileDataSelector(state, { last_name: true });

  const payload = {
    last_name,
    country: drivers_license?.country_code,
    country_subdivision: drivers_license?.country_subdivision_code,
    driver_license_number: drivers_license?.license_number,
  };

  return dispatch(searchProfileByDriverLicense(payload)).then((resp) => {
    // if there's an error in the response, or no response at all, just return null
    if (!resp || resp?.messages?.length) {
      return null;
    }

    const { eligible_for_copy_profile: { copy_address_profile, copy_phones } = {} } = resp;
    if (!copy_address_profile && !copy_phones) {
      return null;
    }

    const { profile, address_profile, contact_profile } = resp;
    const copyProfileData = { copy_from_individual_id: profile.individual_id, copy_address_profile, copy_phones };
    if (copy_address_profile) {
      copyProfileData.address = address_profile;
    }
    if (copy_phones) {
      copyProfileData.phones = contact_profile?.phones;
    }

    return copyProfileData;
  });
};

/**
 * setDriveProfileState
 * gmi action to set values into ui reservations driver_profile
 *
 * @param {object} params
 * @param {string} params.type - Name of the key to be set in driver_profile (default to null, which set all values in batch)
 * @param {object} params.args - Object with driver profile values
 */
export const setDriveProfileState =
  ({ type = null, args = {} }) =>
  (dispatch) =>
    dispatch(actions.reservations.driverProfileState.set(type, args));

/**
 * clearProfile
 * gmi action that clears the profile currently in GMA session and replaces partial profile found with new empty profile with DL request details
 */
export const clearProfile = () => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        service: services.gma.profile.clearProfile,
      }),
      loadingOverlay: true,
    })
  );

/**
 * forgotPasswordProfile action aka resetPasswordStart
 * gmi service call to send user an email with link to reset password
 *
 * @param {object} payload
 * @param {string} payload.first_name - Profile first name
 * @param {string} payload.last_name - Profile last name
 * @param {string} payload.email - Profile email
 * @return {Promise}
 */
export const forgotPasswordProfile = (payload) => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        data: { payload },
        service: services.gma.profile.resetPasswordStart,
      }),
      loadingOverlay: true,
      redirect: true,
    })
  );

/**
 * resetPasswordProfile action aka resetPasswordStart
 * gmi service call to set the new password for the user
 *
 * @param {object} payload
 * @param {string} payload.username - Username or email
 * @param {string} payload.new_password - New password
 * @return {Promise}
 */
export const resetPasswordProfile = (payload) => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        data: { payload },
        service: services.gma.profile.resetPassword,
      }),
      loadingOverlay: true,
      redirect: true,
      onComplete: (response) => {
        if (response?.go_to) {
          // check if the redirect is to auth homepage for successful reset password
          if (response.go_to.parent === WINDOW_OBJECT_KEYS.AUTH_HOME_PAGE) {
            // Analytics event analyticsResetPasswordSuccess
            dispatchEventWithDOM('ResetPasswordSuccess');
          }
        }

        if (
          response?.messages?.length > 0 &&
          response.messages[0]?.code === GMA_RESPONSE_CODES.GMA_RESET_PASSWORD_TOKEN_MISSING
        ) {
          response.messages[0].message = i18n('reset_password_token_missing');
        }

        return response;
      },
    })
  );

/**
 * forgotPasswordProfile action aka forgotUsername
 * gmi service call to send user an email with the username
 *
 * @param {object} payload
 * @param {string} payload.first_name - Profile first name
 * @param {string} payload.last_name - Profile last name
 * @param {string} payload.email - Profile email
 * @return {Promise}
 */
export const forgotUsernameProfile = (payload) => (dispatch) =>
  dispatch(
    gmiRequest({
      action: actions.request({
        data: { payload },
        service: services.gma.profile.forgotUsername,
      }),
      loadingOverlay: true,
      redirect: true,
    })
  );
